<template>
  <!-- question card -->
  <v-card
    class="c-question-card"
    elevation="4"
  >
    <!-- context -->
    <div class="c-context">
      <ClampedText
        v-if="hasContext"
        class="c-context-text c-text pa-4"
        :max-lines="5"
      >
        {{ question.context }}
      </ClampedText>
    </div>

    <!-- reference -->
    <div v-if="hasReference" class="px-4 py-0 text-center">
      <PopupWindow
        :frame="question.reference.frame || false"
        :label="question.reference.name || question.reference.url"
        :src="question.reference.url"
      />
    </div>

    <!-- question -->
    <div class="c-question">
      <v-card-title class="c-question-text c-text font-italic py-2">
        Q{{ qno }}: {{ question.query }}
      </v-card-title>

      <div class="c-question-content d-flex flex-wrap ma-auto">
        <!-- visuals -->
        <v-card-text
          v-if="question.image"
          class="c-figure c-half mx-auto"
        >
          <v-img
            :key="qno"
            class="mx-auto"
            contain
            :src="question.image.url"
          />
          <div class="text-center ma-auto">
            {{ question.image?.caption }}
          </div>
        </v-card-text>

        <!-- options -->
        <v-card-text class="c-options c-half">
          <p class="c-prompt font-weight-bold pb-0 my-0 text-left">
            {{ prompt }}
          </p>
          <v-list class="pa-0" shaped>
            <v-list-item-group
              v-model="selected"
              class="c-option-ol"
              color="accent"
              :multiple="isMultipleChoice"
            >
              <v-list-item
                v-for="(option, index) in options"
                :key="index"
                class="c-option-li"
                :class="{'c-correct': option.correct, 'c-incorrect': !option.correct, 'c-review': review }"
                active-class="c-selected"
                :disabled="review"
              >
                <template #default="{ active }">
                  <v-list-item-icon class="ma-0 pt-3 c-option-text">
                    {{ index | charIndex }}.
                 </v-list-item-icon>

                  <v-list-item-content class="c-option-text c-text">
                    {{ option.answer }}
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-checkbox
                      v-if="isMultipleChoice"
                      :disabled="review"
                      :input-value="active"
                      color="accent"
                    />
                    <v-radio
                      v-else
                      :read-only="review"
                      :off-icon="active ? '$radioOn' : '$radioOff'"
                      color="accent"
                    />
                  </v-list-item-action>
                </template>
              </v-list-item>
            </v-list-item-group>
          </v-list>

          <!-- elaboration -->
          <div
            v-if="review && hasElaboration"
            class="c-elaboration"
          >
            <p class="font-weight-bold text-decoration-underline mt-4 mb-0"> {{ $t('quiz.elaboration') }}</p>
            <RichText :blocks="elaboration"/>
          </div>

          <!-- actions -->

          <!-- review actions -->
          <v-card-actions
            v-if="review"
            class="d-flex justify-space-around pa-4"
          >
            <v-btn
              v-if="!isMobile"
              class="c-btn-invisible"
            >
              {{ $t('quiz.action.return') }}
            </v-btn>

            <v-spacer v-if="!isMobile" />

            <v-btn
              class="mr-4"
              color="primary"
              :disabled="first"
              @click="$emit('prev', selected)"
            >
              {{ $t('quiz.action.prev') }}
            </v-btn>

            <v-btn
              class="mr-4"
              color="primary"
              @click="$emit('next', selected)"
            >
            {{ $t('quiz.action.next') }}
            </v-btn>

            <v-spacer />

            <v-btn
              color="primary"
              @click="$emit('exit')"
            >
              {{ $t('quiz.action.return') }}
            </v-btn>
          </v-card-actions>

          <!-- respond actions -->
          <v-card-actions
            v-else
            class="d-flex justify-center pa-4"
          >
            <v-btn
              class="mr-4"
              color="primary"
              :disabled="first"
              @click="$emit('prev', selected)"
            >
              {{ $t('quiz.action.prev') }}
            </v-btn>

            <v-btn
              class="mr-4"
              color="primary"
              @click="$emit('next', selected)"
            >
              {{ next }}
            </v-btn>
          </v-card-actions>
        </v-card-text>
      </div>
    </div>
  </v-card>
</template>

<script>
import ClampedText from '@/components/base/ClampedText'
import PopupWindow from '@/components/base/PopupWindow'
import RichText from '@/components/base/RichText'

export default {
  name: 'QuizQuestionCard',

  components: {
    ClampedText,
    PopupWindow,
    RichText
  },

  filters: {
    charIndex: function (i) {
      return String.fromCharCode(97 + i)
    }
  },

  props: {
    qno: {
      type: Number,
      required: true
    },

    question: {
      type: Object,
      required: true
    },

    options: {
      type: Array,
      required: true
    },

    response: {
      type: [Number, Array],
      required: false,
      default: null
    },

    first: {
      type: Boolean,
      required: false,
      default: false
    },

    last: {
      type: Boolean,
      required: false,
      default: false
    },

    review: {
      type: Boolean,
      required: false,
      default: false
    },

    elaboration: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  data: function () {
    return {
      selected: this.response
    }
  },

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mobile
    },

    isMultipleChoice() {
      return this.options.filter((option) => option.correct).length > 1
    },

    hasContext() {
      return this.question.context?.length > 0
    },

    hasElaboration() {
      return this.elaboration?.length > 0
    },

    hasReference() {
      return this.question.reference?.url?.length > 0
    },

    prompt() {
      return this.isMultipleChoice ? this.$t('quiz.prompt.multiple') : this.$t('quiz.prompt.single')
    },

    next() {
      return this.last
        ? this.$t('quiz.action.submit')
        : this.selected != null
        ? this.$t('quiz.action.next')
        : this.$t('quiz.action.skip')
    }
  },

  created: function () {
    // v-list requires an empty array for multiple choice selector
    this.selected = this.isMultipleChoice ? this.response || [] : this.response
  },

  methods: {
    viewReference(url) {
      window.open(url, '_blank', 'width=300, height=250')
    }
  }
}
</script>

<style lang="css" scoped>
.c-question-card {
  height: 100%;
  width: 100%;
  white-space: normal;
}
.c-xtext {
  white-space: pre-wrap;
}
.c-context-text,
.c-prompt,
.c-question-text {
  line-height: 1.5;
  font-size: 1.2rem;
  font-weight: 400;
}
.c-half {
  flex: 1 1 50%;
}
.c-figure {
  min-width: 300px;
  max-width: 50%;
}
.c-prompt,
.c-option-ol {
  list-style-type: lower-alpha;
  list-style-position: outside;
}
.c-option-li {
}
.c-option-text {
  font-size: 1.25rem;
}
.c-selected {
  background-color: var(--c-primary-light);
}

/* review mode styles */
.c-review.c-correct {
  color: white !important;
  background-color: var(--v-success-base);
}
.c-review.c-correct .v-input {
  color: white !important;
}
.c-review.c-selected.c-incorrect {
  color: white;
  background-color: var(--v-error-base);
}
.c-review.c-selected.c-incorrect .v-input {
  color: white;
}

/* elaboration */
.c-elaboration {
  font-size: 1.2rem;
  line-height: 1.5;
}

.c-elaboration :deep(*) {
  font-size: 1.2rem;
  line-height: 1.5 !important;
}
</style>
